import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdReceipt, MdCheckCircle, MdError, MdPending, MdAttachMoney, MdAccessTime, MdRefresh, MdStars, MdAssessment, MdLocationOn, MdMap, MdPublic, MdPeople, MdGavel, MdCreditCard, MdCancel, MdDownload, MdAutorenew } from 'react-icons/md';
import { formatVoterCount } from '../utils/formatVoterCount';

function TransactionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [candidacyReports, setCandidacyReports] = useState([]);
  const [candidacyPricing, setCandidacyPricing] = useState([]);
  const [areaCounts, setAreaCounts] = useState({ local: 0, state: 0, national: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [purchaseMessage, setPurchaseMessage] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'candidacy' ? 'candidacy' : searchParams.get('tab') === 'subscriptions' ? 'subscriptions' : 'transactions');
  const [disclaimerLevel, setDisclaimerLevel] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subTransactions, setSubTransactions] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [candidacyPromoCode, setCandidacyPromoCode] = useState('');
  const [candidacyPromoStatus, setCandidacyPromoStatus] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [txRes, reportsRes, pricingRes, countsRes, subRes, subTxRes] = await Promise.all([
        axios.get(`${API_URL}/api/transactions`),
        axios.get(`${API_URL}/api/candidacy/reports`),
        axios.get(`${API_URL}/api/candidacy/pricing`),
        axios.get(`${API_URL}/api/candidacy/area-counts`),
        axios.get(`${API_URL}/api/subscription/my`),
        axios.get(`${API_URL}/api/subscription/transactions`),
      ]);
      setTransactions(txRes.data.transactions || []);
      setCandidacyReports(reportsRes.data.reports || []);
      setCandidacyPricing(pricingRes.data.pricing || []);
      setAreaCounts(countsRes.data || { local: 0, state: 0, national: 0 });
      setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);
      setSubTransactions(subTxRes.data.transactions || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const purchaseReport = async (level) => {
    // Show disclaimer first
    setDisclaimerLevel(level);
  };

  const confirmPurchase = async () => {
    const level = disclaimerLevel;
    setDisclaimerLevel(null);
    setPurchaseLoading(level);
    setPurchaseMessage('');
    try {
      const res = await axios.post(`${API_URL}/api/candidacy/purchase`, {
        level,
        origin_url: window.location.origin,
        promo_code: candidacyPromoCode || null,
      });

      if (res.data.requires_payment && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      setPurchaseMessage(res.data.message);
      setCandidacyPromoCode('');
      setCandidacyPromoStatus(null);
      const reportsRes = await axios.get(`${API_URL}/api/candidacy/reports`);
      setCandidacyReports(reportsRes.data.reports || []);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to purchase report';
      setPurchaseMessage(detail);
    } finally {
      setPurchaseLoading(null);
    }
  };

  const validateCandidacyPromo = async () => {
    if (!candidacyPromoCode.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/promo-codes/validate`, {
        code: candidacyPromoCode, purchase_type: 'candidacy',
      });
      setCandidacyPromoStatus({ type: 'success', message: res.data.message, data: res.data });
    } catch (err) {
      setCandidacyPromoStatus({ type: 'error', message: err.response?.data?.detail || 'Invalid code' });
    }
  };

  const isPointsPurchase = (tx) => tx.currency === 'points' || tx.sessionId?.startsWith('points_');

  const getStatusBadge = (paymentStatus, pollStatus) => {
    if (paymentStatus === 'paid') {
      if (pollStatus === 'active') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><MdCheckCircle className="text-sm" />Active</span>;
      if (pollStatus === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"><MdPending className="text-sm" />Pending Review</span>;
      if (pollStatus === 'rejected') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><MdError className="text-sm" />Rejected</span>;
      if (pollStatus === 'closed') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><MdCheckCircle className="text-sm" />Completed</span>;
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><MdCheckCircle className="text-sm" />Paid</span>;
    }
    if (paymentStatus === 'initiated' || paymentStatus === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><MdPending className="text-sm" />Pending</span>;
    if (paymentStatus === 'expired') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"><MdError className="text-sm" />Expired</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><MdError className="text-sm" />Failed</span>;
  };

  const getPaymentTypeBadge = (tx) => {
    if (isPointsPurchase(tx)) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><MdStars className="text-sm" />Points</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><MdAttachMoney className="text-sm" />Card</span>;
  };

  const getDurationLabel = (days) => {
    if (days === 1) return '1 Day Poll';
    if (days === 7) return '7 Day Poll';
    if (days === 30) return '30 Day Poll';
    return `${days} Day Poll`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatAmount = (tx) => {
    if (isPointsPurchase(tx)) return `${tx.pointsUsed || getPointsForDuration(tx.durationDays)} pts`;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tx.amount);
  };

  const getPointsForDuration = (days) => ({ 1: 20, 7: 100, 30: 500 }[days] || 0);

  const cancelSubscription = async (immediately) => {
    setCancelLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscription/cancel`, { cancel_immediately: immediately });
      setShowCancelModal(false);
      fetchAll();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
    } finally {
      setCancelLoading(false);
    }
  };

  const downloadReceipt = async (txId) => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/transactions/${txId}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CIVOTE_Receipt_${txId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading receipt:', err);
    }
  };

  const getLevelIcon = (level) => {
    if (level === 'local') return <MdLocationOn className="text-xl" />;
    if (level === 'state') return <MdMap className="text-xl" />;
    return <MdPublic className="text-xl" />;
  };

  const getReportStatusBadge = (status) => {
    if (status === 'completed') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><MdCheckCircle className="text-sm" />Completed</span>;
    if (status === 'processing' || status === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>Processing</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><MdError className="text-sm" />Failed</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 py-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <MdReceipt className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-rubik">Transactions</h1>
            <p className="text-sm text-gray-400 font-rubik">Your purchases & candidacy reports</p>
          </div>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white hover:bg-white/20 rounded-card transition-colors" data-testid="refresh-transactions-btn">
          <MdRefresh className="text-lg" /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-900/50 border border-red-500/50 rounded-card p-4 mb-6 text-red-300 font-rubik">{error}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-white/10 rounded-lg p-1 mb-6" data-testid="transaction-tabs">
        <button
          onClick={() => setActiveTab('transactions')}
          className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'transactions' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
          data-testid="tab-transactions"
        >
          Poll Purchases ({transactions.length})
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'subscriptions' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
          data-testid="tab-subscriptions"
        >
          Subscriptions ({subTransactions.length})
        </button>
        <button
          onClick={() => setActiveTab('candidacy')}
          className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'candidacy' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
          data-testid="tab-candidacy"
        >
          Candidacy Reports ({candidacyReports.length})
        </button>
      </div>

      {/* Poll Purchases Tab */}
      {activeTab === 'transactions' && (
        <>
          {transactions.length === 0 ? (
            <div className="bg-white rounded-card shadow-sm border p-8 text-center">
              <MdReceipt className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 font-rubik mb-2">No Transactions Yet</h3>
              <p className="text-gray-500 font-rubik">Your poll purchase history will appear here once you create a custom poll.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.map((tx) => (
                <div key={tx.transactionId} className="bg-white rounded-card shadow-sm border p-5 hover:shadow-md transition-shadow" data-testid={`transaction-${tx.transactionId}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPointsPurchase(tx) ? 'bg-orange-100' : 'bg-green-100'}`}>
                          {isPointsPurchase(tx) ? <MdStars className="text-tertiary text-xl" /> : <MdAttachMoney className="text-primary text-xl" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 mb-1">{tx.pollQuestion}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1"><MdAccessTime className="text-sm" />{getDurationLabel(tx.durationDays)}</span>
                            <span className="text-gray-300">&#8226;</span>
                            <span>{formatDate(tx.createdAt)}</span>
                            <span className="text-gray-300">&#8226;</span>
                            {getPaymentTypeBadge(tx)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 md:gap-6">
                      <div className="text-right">
                        <p className={`text-lg font-bold font-rubik ${isPointsPurchase(tx) ? 'text-tertiary' : 'text-gray-800'}`}>{formatAmount(tx)}</p>
                        <p className="text-xs text-gray-500 font-rubik uppercase">{isPointsPurchase(tx) ? 'Poll Points' : tx.currency}</p>
                      </div>
                      <div className="min-w-[100px] text-right">{getStatusBadge(tx.paymentStatus, tx.pollStatus)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {transactions.length > 0 && (
            <div className="mt-6 bg-gray-50 rounded-card p-4 border">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600 font-rubik">Total Transactions</p>
                  <p className="text-xl font-bold text-gray-800 font-rubik">{transactions.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-rubik">Points Used</p>
                  <p className="text-xl font-bold text-tertiary font-rubik">
                    {transactions.filter(tx => isPointsPurchase(tx)).reduce((sum, tx) => sum + (tx.pointsUsed || getPointsForDuration(tx.durationDays)), 0)} pts
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-rubik">Money Spent</p>
                  <p className="text-xl font-bold text-primary font-rubik">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      transactions.filter(tx => !isPointsPurchase(tx)).reduce((sum, tx) => sum + (tx.amount || 0), 0)
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <>
          {/* Current Subscription Status */}
          {subscription && (
            <div className="bg-white rounded-card shadow-card border p-6 mb-6" data-testid="subscription-status-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <MdCreditCard className="text-primary text-xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 font-rubik">Current Subscription</h2>
                    <p className="text-xs text-gray-500 font-rubik">{subscription.plan_name} Plan</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                  subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                  subscription.status === 'cancelling' ? 'bg-amber-100 text-amber-800' :
                  subscription.status === 'exempt' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-600'
                }`} data-testid="subscription-status-badge">
                  {subscription.status === 'active' && <MdCheckCircle className="text-sm" />}
                  {subscription.status === 'exempt' && <MdCheckCircle className="text-sm" />}
                  {subscription.status === 'cancelling' && <MdAutorenew className="text-sm" />}
                  {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 font-rubik">Amount</p>
                  <p className="text-sm font-semibold text-gray-800 font-rubik">{subscription.billing_exempt ? 'Exempt' : `$${subscription.amount?.toFixed(2)}`}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-rubik">Started</p>
                  <p className="text-sm font-semibold text-gray-800 font-rubik">{formatDate(subscription.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-rubik">Current Period Ends</p>
                  <p className="text-sm font-semibold text-gray-800 font-rubik">{subscription.current_period_end ? formatDate(subscription.current_period_end) : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-rubik">Last Payment</p>
                  <p className="text-sm font-semibold text-gray-800 font-rubik">{subscription.last_payment_date ? formatDate(subscription.last_payment_date) : 'N/A'}</p>
                </div>
              </div>

              {subscription.status === 'cancelling' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 font-rubik">
                  Your subscription will end on {formatDate(subscription.current_period_end)}. Your representative profile will remain active until then.
                </div>
              )}

              {subscription.status === 'active' && !subscription.billing_exempt && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-sm font-rubik transition-colors"
                  data-testid="cancel-subscription-btn"
                >
                  <MdCancel className="text-lg" /> Cancel Subscription
                </button>
              )}
            </div>
          )}

          {/* Subscription Transaction History */}
          {subTransactions.length === 0 ? (
            <div className="bg-white rounded-card shadow-sm border p-8 text-center">
              <MdCreditCard className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 font-rubik mb-2">No Subscription Transactions</h3>
              <p className="text-gray-500 font-rubik">Your subscription billing history will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white font-rubik">Billing History</h2>
              {subTransactions.map((tx) => (
                <div key={tx.tx_id} className="bg-white rounded-card shadow-sm border p-5 hover:shadow-md transition-shadow" data-testid={`sub-tx-${tx.tx_id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tx.type === 'renewal' ? 'bg-blue-100' : 'bg-green-100'}`}>
                        {tx.type === 'renewal' ? <MdAutorenew className="text-blue-600 text-xl" /> : <MdCreditCard className="text-primary text-xl" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800 font-rubik">{tx.plan_name} Subscription {tx.type === 'renewal' ? 'Renewal' : 'Payment'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{formatDate(tx.created_at)}</span>
                          <span className="text-gray-300">&bull;</span>
                          <span>{new Date(tx.billing_period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(tx.billing_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-800 font-rubik">${tx.amount?.toFixed(2)}</p>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <MdCheckCircle className="text-xs" /> Paid
                        </span>
                      </div>
                      <button
                        onClick={() => downloadReceipt(tx.tx_id)}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-xs font-rubik transition-colors"
                        title="Download PDF Receipt"
                        data-testid={`download-receipt-${tx.tx_id}`}
                      >
                        <MdDownload className="text-sm" /> PDF
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Candidacy Reports Tab */}
      {activeTab === 'candidacy' && (
        <>
          {/* Purchase Section */}
          <div className="bg-white rounded-card shadow-card border p-6 mb-6" data-testid="candidacy-purchase-section">
            <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-1">Run a Candidacy Assessment</h2>
            <p className="text-sm text-gray-500 font-rubik mb-4">See how your poll responses align with voters at different levels. Limited to 1 report per day.</p>

            {purchaseMessage && (
              <div className={`rounded-card p-3 mb-4 text-sm font-rubik ${purchaseMessage.includes('Failed') || purchaseMessage.includes('only generate') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`} data-testid="purchase-message">
                {purchaseMessage}
              </div>
            )}

            <div className="flex items-end gap-2 mb-4" data-testid="candidacy-promo-section">
              <div className="flex-grow">
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Promo Code</label>
                <input
                  type="text"
                  value={candidacyPromoCode}
                  onChange={e => { setCandidacyPromoCode(e.target.value.toUpperCase()); setCandidacyPromoStatus(null); }}
                  placeholder="Enter promo code"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  data-testid="candidacy-promo-input"
                />
              </div>
              <button
                onClick={validateCandidacyPromo}
                disabled={!candidacyPromoCode.trim()}
                className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors"
                data-testid="candidacy-promo-apply-btn"
              >
                Apply
              </button>
            </div>
            {candidacyPromoStatus && (
              <div className={`text-sm rounded-lg p-3 mb-4 font-rubik ${candidacyPromoStatus.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="candidacy-promo-status">
                {candidacyPromoStatus.message}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {candidacyPricing.map((p) => {
                const voterCount = areaCounts[p.level] || 0;
                const minVoters = 100;
                const isDisabled = voterCount < minVoters;
                return (
                <div 
                  key={p.level} 
                  className={`border rounded-lg p-4 transition-colors ${isDisabled ? 'opacity-50 bg-gray-50 border-gray-200' : 'hover:border-primary'}`} 
                  data-testid={`candidacy-option-${p.level}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDisabled ? 'bg-gray-200 text-gray-400' : 'bg-primary/10 text-primary'}`}>
                      {getLevelIcon(p.level)}
                    </div>
                    <h3 className={`font-medium font-rubik capitalize ${isDisabled ? 'text-gray-400' : 'text-gray-800'}`}>{p.level}</h3>
                  </div>
                  <p className={`text-xs font-rubik mb-2 min-h-[32px] ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isDisabled ? (p.level === 'local' ? 'Requires 100+ local participants for meaningful results.' : 'Requires 100+ participants in your area for meaningful results.') : p.description}
                  </p>
                  <div className="flex items-center gap-1.5 mb-3">
                    <MdPeople className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-tertiary'}`} />
                    <span className={`text-xs font-medium font-rubik ${isDisabled ? 'text-gray-400' : 'text-tertiary'}`} data-testid={`voter-count-${p.level}`}>
                      {formatVoterCount(voterCount)} voters in your area
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-bold font-rubik ${isDisabled ? 'text-gray-400' : 'text-gray-800'}`}>
                      {p.price === 0 ? 'Free' : `$${p.price.toFixed(2)}`}
                    </span>
                    {isDisabled ? (
                      <span className="px-3 py-1.5 bg-gray-200 text-gray-400 text-xs font-medium rounded-lg font-rubik cursor-not-allowed" data-testid={`purchase-${p.level}-btn`}>
                        Unavailable
                      </span>
                    ) : (
                      <button
                        onClick={() => purchaseReport(p.level)}
                        disabled={purchaseLoading === p.level || !p.enabled}
                        className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors font-rubik"
                        data-testid={`purchase-${p.level}-btn`}
                      >
                        {purchaseLoading === p.level ? 'Processing...' : (p.price > 0 ? 'Purchase' : 'Generate')}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Reports List */}
          {candidacyReports.length === 0 ? (
            <div className="bg-white rounded-card shadow-sm border p-8 text-center">
              <MdAssessment className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 font-rubik mb-2">No Reports Yet</h3>
              <p className="text-gray-500 font-rubik">Generate a candidacy assessment above to see how you align with voters in your area.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white font-rubik">Your Reports</h2>
              {candidacyReports.map((r) => (
                <button
                  key={r.reportId}
                  onClick={() => r.status === 'completed' && navigate(`/candidacy-report/${r.reportId}`)}
                  className={`w-full bg-white rounded-card shadow-sm border p-5 text-left transition-shadow ${r.status === 'completed' ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}
                  data-testid={`report-${r.reportId}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-tertiary/10 flex items-center justify-center text-tertiary">
                        {getLevelIcon(r.level)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800 font-rubik capitalize">{r.level} Candidacy Assessment</p>
                        <p className="text-xs text-gray-500 font-rubik">{formatDate(r.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {r.amountPaid > 0 && (
                        <span className="text-sm font-bold text-gray-800 font-rubik">${r.amountPaid.toFixed(2)}</span>
                      )}
                      {getReportStatusBadge(r.status)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Legal Disclaimer Modal */}
      {disclaimerLevel && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="disclaimer-modal-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" data-testid="disclaimer-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <MdGavel className="text-amber-600 text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 font-rubik">Legal Disclaimer</h3>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-200">
              <p className="text-sm text-gray-700 font-rubik leading-relaxed mb-3">
                This Candidacy Assessment evaluates your poll responses exclusively against other registered users on this platform. It does <strong>not</strong> represent a scientific poll, official survey, or certified political analysis.
              </p>
              <p className="text-sm text-gray-700 font-rubik leading-relaxed mb-3">
                Results are based on a point-in-time snapshot of platform user responses and are subject to change. The assessment does not account for the opinions or demographics of the general public, eligible voters, or any official electorate.
              </p>
              <p className="text-sm text-gray-700 font-rubik leading-relaxed">
                This report is provided for informational purposes only and should not be relied upon as the sole basis for any political candidacy decision.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDisclaimerLevel(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-rubik"
                data-testid="disclaimer-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurchase}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors font-rubik"
                data-testid="disclaimer-acknowledge-btn"
              >
                I Acknowledge & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="cancel-sub-modal-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" data-testid="cancel-sub-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                <MdCancel className="text-red-600 text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 font-rubik">Cancel Subscription</h3>
            </div>
            <p className="text-sm text-gray-600 font-rubik mb-5">How would you like to cancel your subscription?</p>
            <div className="space-y-3 mb-5">
              <button
                onClick={() => cancelSubscription(false)}
                disabled={cancelLoading}
                className="w-full flex items-between gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-amber-400 transition-colors text-left"
                data-testid="cancel-at-period-end-btn"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800 font-rubik">Cancel at end of billing period</p>
                  <p className="text-xs text-gray-500 font-rubik mt-1">Your profile stays active until {subscription?.current_period_end ? formatDate(subscription.current_period_end) : 'the end of your billing period'}.</p>
                </div>
              </button>
              <button
                onClick={() => cancelSubscription(true)}
                disabled={cancelLoading}
                className="w-full flex items-between gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 transition-colors text-left"
                data-testid="cancel-immediately-btn"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-700 font-rubik">Cancel immediately</p>
                  <p className="text-xs text-gray-500 font-rubik mt-1">Your representative profile will be deactivated right away.</p>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowCancelModal(false)}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-rubik"
              data-testid="cancel-sub-modal-close"
            >
              Keep My Subscription
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionsPage;
